/**
 * Turns decrypted Hub activity summaries into the bucket/event shape
 * `vis-timeline` (and the shared timeline helpers: tooltips, colors,
 * swimlanes, row labels) already understand from local aw-server buckets.
 */
/**
 * Reconstructs absolute timestamps for one summary's events.
 *
 * The agent samples once a second and merges consecutive identical samples,
 * so a summary's events are contiguous and in chronological order, and the
 * last one ends when the summary was sent (`window_end`). Walking backwards
 * from `window_end` places every event exactly. `window_start` is not used:
 * it's nominal (`window_end` minus the send interval), and after an agent
 * restart the real sampled span is shorter than that.
 */
export function eventsWithTimestamps(summary) {
    // Interval payloads carry each event's real start: their events are not
    // contiguous, so walking back from window_end would misplace them.
    if (summary.events.every(e => e.start !== undefined)) {
        return summary.events.map(e => ({
            afk: e.afk,
            app: e.app,
            recorded_app: e.recorded_app ?? null,
            bundle_id: e.bundle_id ?? null,
            domain: e.domain ?? null,
            private_browsing: e.private_browsing === true,
            title: e.title,
            start: e.start,
            duration: e.duration_secs,
        }));
    }
    const total = summary.events.reduce((sum, e) => sum + e.duration_secs, 0);
    let cursor = summary.window_end - total;
    return summary.events.map(e => {
        const placed = {
            afk: e.afk,
            app: e.app,
            recorded_app: e.recorded_app ?? null,
            bundle_id: e.bundle_id ?? null,
            domain: e.domain ?? null,
            private_browsing: e.private_browsing === true,
            title: e.title,
            start: cursor,
            duration: e.duration_secs,
        };
        cursor += e.duration_secs;
        return placed;
    });
}
/**
 * One window row and one AFK row per device, holding only events that
 * overlap `[startUnix, endUnix)`. Bucket ids follow the local
 * `aw-watcher-<type>_<suffix>` convention so the shared row-label helper
 * shortens them to "window" / "afk"; the device id (not hostname) is the
 * suffix because several devices can share a hostname.
 */
export function toTimelineBuckets(devices, summariesByDevice, startUnix, endUnix) {
    const buckets = [];
    for (const device of devices) {
        const hostname = device.hostname || 'Unknown host';
        const windowEvents = [];
        const afkEvents = [];
        for (const summary of summariesByDevice[device.id] ?? []) {
            for (const e of eventsWithTimestamps(summary)) {
                if (e.start >= endUnix || e.start + e.duration <= startUnix)
                    continue;
                const timestamp = new Date(e.start * 1000).toISOString();
                afkEvents.push({
                    timestamp,
                    duration: e.duration,
                    data: { status: e.afk ? 'afk' : 'not-afk' },
                });
                if (!e.afk) {
                    windowEvents.push({
                        timestamp,
                        duration: e.duration,
                        data: {
                            app: e.app ?? 'unknown',
                            title: e.title ?? '',
                            recorded_app: e.recorded_app,
                            ...(e.bundle_id ? { bundle_id: e.bundle_id } : {}),
                            device: device.id,
                            domain: e.domain ?? null,
                        },
                    });
                }
            }
        }
        buckets.push({
            id: `aw-watcher-window_${device.id}`,
            type: 'currentwindow',
            hostname,
            events: windowEvents,
        }, { id: `aw-watcher-afk_${device.id}`, type: 'afkstatus', hostname, events: afkEvents });
    }
    return buckets;
}
/**
 * The span worth showing: from the first event's start to the last event's
 * end across `buckets`, padded by 5% of that span (at least a minute) on
 * each side and clamped to the selected range. Falls back to the selected
 * range when there's nothing to show. Times are epoch milliseconds.
 */
export function activeInterval(buckets, rangeStartMs, rangeEndMs) {
    let first = Infinity;
    let last = -Infinity;
    for (const bucket of buckets) {
        for (const e of bucket.events) {
            const start = new Date(e.timestamp).getTime();
            first = Math.min(first, start);
            last = Math.max(last, start + e.duration * 1000);
        }
    }
    if (first === Infinity)
        return [rangeStartMs, rangeEndMs];
    const pad = Math.max((last - first) * 0.05, 60 * 1000);
    return [Math.max(rangeStartMs, first - pad), Math.min(rangeEndMs, last + pad)];
}
/** Collapses adjacent same-app window events separated by less than
 * `maxGapSecs` — the Hub-data equivalent of the local Timeline's
 * "merge by app" filter. Returns new event objects; input is untouched. */
export function mergeAdjacentByApp(events, maxGapSecs = 30) {
    if (events.length <= 1)
        return events.map(e => ({ ...e, data: { ...e.data } }));
    const sorted = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const merged = [];
    let current = { ...sorted[0], data: { ...sorted[0].data } };
    for (const next of sorted.slice(1)) {
        const currentStart = new Date(current.timestamp).getTime();
        const currentEnd = currentStart + current.duration * 1000;
        const nextStart = new Date(next.timestamp).getTime();
        const nextEnd = nextStart + next.duration * 1000;
        if (current.data.app === next.data.app && nextStart - currentEnd < maxGapSecs * 1000) {
            current.duration = (Math.max(currentEnd, nextEnd) - currentStart) / 1000;
        }
        else {
            merged.push(current);
            current = { ...next, data: { ...next.data } };
        }
    }
    merged.push(current);
    return merged;
}
